import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { PageHead, Skel, Empty } from '../components/ui'
import { thaiDate } from '../lib/format'

interface Client {
  id: string
  code: string
  name: string
  tax_id?: string
  industry?: string
  status: string
  created_at: string
}

export function Clients() {
  const { me } = useAuth()
  const canCreate = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['clients'], queryFn: () => api<Client[]>('/v1/clients') })

  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [taxId, setTaxId] = useState('')
  const [industry, setIndustry] = useState('')
  const [err, setErr] = useState('')

  const create = useMutation({
    mutationFn: () => apiWrite<Client>('/v1/clients', 'POST', { code, name, tax_id: taxId, industry }),
    onSuccess: () => {
      setOpen(false); setCode(''); setName(''); setTaxId(''); setIndustry(''); setErr('')
      void qc.invalidateQueries({ queryKey: ['clients'] })
    },
    onError: (e) => setErr((e as Error).message),
  })

  return (
    <>
      <PageHead title="นิติบุคคล" sub="ลูกค้าที่อยู่ภายใต้การตรวจสอบขององค์กรคุณ"
        actions={canCreate ? <button className="btn btn-primary" onClick={() => setOpen((v) => !v)}>
          {open ? 'ปิดฟอร์ม' : 'เพิ่มนิติบุคคล'}
        </button> : undefined} />

      {open && (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="card-label">เพิ่มนิติบุคคลใหม่</div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-code">รหัสลูกค้า</label>
              <input id="c-code" className="input mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CL-0042" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-name">ชื่อนิติบุคคล</label>
              <input id="c-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="บริษัท ตัวอย่าง จำกัด" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-tax">เลขประจำตัวผู้เสียภาษี</label>
              <input id="c-tax" className="input mono" inputMode="numeric" maxLength={13} value={taxId}
                onChange={(e) => setTaxId(e.target.value.replace(/\D/g, ''))} placeholder="0105558000000" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="c-ind">ประเภทธุรกิจ</label>
              <input id="c-ind" className="input" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="การผลิต / ค้าปลีก" />
            </div>
          </div>
          {err && <div className="form-error">{err}</div>}
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button className="btn btn-primary" disabled={create.isPending || !code || !name}
              onClick={() => { setErr(''); create.mutate() }}>{create.isPending ? 'กำลังบันทึก…' : 'บันทึก'}</button>
            <span className="dim" style={{ fontSize: 11.5 }}>รหัสและชื่อจำเป็นต้องกรอก</span>
          </div>
        </div>
      )}

      {q.isLoading ? <Skel h={200} /> : (q.data ?? []).length === 0 ? (
        <Empty title="ยังไม่มีนิติบุคคล" body="เพิ่มลูกค้ารายแรกเพื่อเริ่มโครงการตรวจสอบ" />
      ) : (
        <div className="table-wrap card">
          <table className="data">
            <thead>
              <tr>
                <th>รหัส</th><th>ชื่อนิติบุคคล</th><th>เลขผู้เสียภาษี</th><th>ประเภทธุรกิจ</th>
                <th>สถานะ</th><th className="num">เพิ่มเมื่อ</th>
              </tr>
            </thead>
            <tbody>
              {(q.data ?? []).map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.code}</td>
                  <td>{c.name}</td>
                  <td className="mono dim">{c.tax_id || '—'}</td>
                  <td className="dim">{c.industry || '—'}</td>
                  <td><span className={`badge badge-${c.status === 'active' ? 'closed' : 'received'}`}>{c.status === 'active' ? 'ใช้งาน' : c.status}</span></td>
                  <td className="num dim">{thaiDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
